import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { MapPin, BedDouble, Building2, Award, ArrowUpRight, ArrowLeft } from "lucide-react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/primitives";
import { CTABand } from "../components/CTABand";
import { API } from "../lib/site";

const Directory = () => {
    const [hotels, setHotels] = useState(null);

    useEffect(() => {
        axios.get(`${API}/hotels/directory`).then((res) => setHotels(res.data.hotels || [])).catch(() => setHotels([]));
    }, []);

    return (
        <main>
            <SEO
                title="Member Hotels | Global Green Hotel"
                description="Hotels participating in the GGH sustainability program — public profiles, progress and recognition."
            />
            <PageHero
                testid="hotels-hero"
                eyebrow="GGH Member Hotels"
                titleLines={["Hotels turning sustainability", "into measurable progress"]}
                description="Public profiles of hotels participating in the GGH sustainability program. Membership is growing — profiles appear here as hotels join and choose to publish."
            />
            <section data-testid="hotels-directory" className="py-24 sm:py-32">
                <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                    {hotels === null ? (
                        <p className="text-sm text-[#4A5D54]">Loading member hotels…</p>
                    ) : hotels.length === 0 ? (
                        <Reveal>
                            <div data-testid="hotels-empty" className="rounded-2xl border border-[#E8E6E1] bg-white p-10">
                                <p className="font-display text-2xl text-[#0A1711]">Founding members are joining now</p>
                                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#4A5D54] md:text-base">
                                    GGH membership has just opened. Hotel profiles will appear here
                                    as participating hotels complete their assessments and publish
                                    their sustainability progress. Your hotel could be among the
                                    first.
                                </p>
                                <Link to="/join" data-testid="hotels-join-cta" className="btn-green mt-7">
                                    Join GGH
                                    <ArrowUpRight size={16} aria-hidden="true" />
                                </Link>
                            </div>
                        </Reveal>
                    ) : (
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="hotels-grid">
                            {hotels.map((h, i) => (
                                <Reveal key={h.id} delay={0.06 * i} y={22}>
                                    <Link
                                        to={`/hotels/${h.id}`}
                                        data-testid={`hotel-card-${h.id}`}
                                        className="card-lift block h-full rounded-2xl border border-[#E8E6E1] bg-white p-7"
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1A3B2B] text-[#D9B96A]">
                                                <Building2 size={19} aria-hidden="true" />
                                            </span>
                                            {h.recognized && (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-[#C2A160] px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-[#0A1711]">
                                                    <Award size={11} aria-hidden="true" /> GGH Recognition
                                                </span>
                                            )}
                                        </div>
                                        <h3 className="font-display mt-5 text-2xl font-medium text-[#0A1711]">{h.hotel_name}</h3>
                                        <p className="mt-2 flex items-center gap-1.5 text-xs text-[#4A5D54]">
                                            <MapPin size={13} className="text-[#C2A160]" aria-hidden="true" /> {h.location}
                                        </p>
                                        <p className="mt-1 flex items-center gap-1.5 text-xs text-[#4A5D54]">
                                            <BedDouble size={13} className="text-[#C2A160]" aria-hidden="true" /> {h.hotel_type}
                                        </p>
                                        {h.overall != null && (
                                            <p className="mt-4 text-xs font-extrabold uppercase tracking-wider text-[#1A3B2B]">
                                                GGH Sustainability Score: <span className="font-display text-base text-[#A4803F]">{h.overall}%</span>
                                            </p>
                                        )}
                                    </Link>
                                </Reveal>
                            ))}
                        </div>
                    )}
                </div>
            </section>
            <CTABand
                testid="cta-band-hotels"
                heading="Is Your Hotel Ready to Measure, Improve, Save?"
                text="Join GGH and demonstrate your impact with a credible, practical sustainability program."
                primary={{ to: "/join", label: "Join GGH" }}
                secondary={{ to: "/for-hotels", label: "Explore Membership" }}
            />
        </main>
    );
};

const Profile = () => {
    const { hotelId } = useParams();
    const [hotel, setHotel] = useState(null);
    const [missing, setMissing] = useState(false);

    useEffect(() => {
        axios
            .get(`${API}/hotels/public/${hotelId}`)
            .then((res) => setHotel(res.data))
            .catch(() => setMissing(true));
    }, [hotelId]);

    return (
        <main>
            <SEO
                title={hotel ? `${hotel.hotel_name} | GGH Member Hotel` : "Hotel Profile | Global Green Hotel"}
                description="A public GGH hotel sustainability profile."
            />
            <div className="grain min-h-screen bg-[#04150D] py-32">
                <div className="relative z-[2] mx-auto max-w-[900px] px-5 sm:px-8">
                    <Link to="/hotels" data-testid="profile-back" className="inline-flex items-center gap-2 text-sm font-bold text-[#D9B96A] hover:underline">
                        <ArrowLeft size={15} aria-hidden="true" /> All Member Hotels
                    </Link>
                    {missing ? (
                        <p data-testid="profile-missing" className="mt-10 text-[#FDFBF7]/70">
                            This hotel profile is not available.
                        </p>
                    ) : !hotel ? (
                        <p className="mt-10 text-sm text-[#FDFBF7]/50">Loading profile…</p>
                    ) : (
                        <Reveal>
                            <div data-testid="hotel-profile" className="mt-8 rounded-2xl border border-[#C2A160]/25 bg-[#07211A] p-8 sm:p-12">
                                <div className="flex flex-wrap items-center gap-3">
                                    <Eyebrow dark>GGH Member Hotel</Eyebrow>
                                    {hotel.recognized && (
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#C2A160] px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-[#0A1711]">
                                            <Award size={11} aria-hidden="true" /> GGH Recognition
                                        </span>
                                    )}
                                </div>
                                <h1 className="font-display mt-4 text-4xl font-medium tracking-tight text-[#FDFBF7] sm:text-5xl">
                                    {hotel.hotel_name}
                                </h1>
                                <p className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#FDFBF7]/60">
                                    <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-[#C2A160]" aria-hidden="true" />{hotel.location}</span>
                                    <span className="inline-flex items-center gap-1.5"><Building2 size={14} className="text-[#C2A160]" aria-hidden="true" />{hotel.hotel_type}</span>
                                    {hotel.rooms && (
                                        <span className="inline-flex items-center gap-1.5"><BedDouble size={14} className="text-[#C2A160]" aria-hidden="true" />{hotel.rooms} rooms</span>
                                    )}
                                    {hotel.member_since && <span>GGH member since {hotel.member_since}</span>}
                                </p>
                                {hotel.overall != null && (
                                    <div className="mt-8">
                                        <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#C2A160]">
                                            GGH Sustainability Score
                                        </p>
                                        <div className="mt-3 flex items-center gap-4">
                                            <span className="font-display text-4xl text-[#FDFBF7]">{hotel.overall}%</span>
                                            <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/10">
                                                <div className="h-full rounded-full bg-[#C2A160]" style={{ width: `${hotel.overall}%` }} />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {hotel.description && (
                                    <p className="mt-8 whitespace-pre-line border-t border-white/10 pt-8 text-base leading-relaxed text-[#FDFBF7]/75">
                                        {hotel.description}
                                    </p>
                                )}
                                <p className="mt-10 border-t border-white/10 pt-6 text-xs leading-relaxed text-[#FDFBF7]/40">
                                    GGH membership and GGH Recognition indicate participation in the
                                    Global Green Hotel sustainability program. They are not
                                    government certification, accreditation, or regulatory approval.
                                </p>
                            </div>
                        </Reveal>
                    )}
                </div>
            </div>
        </main>
    );
};

export const HotelProfile = Profile;
export default Directory;
